/* eslint-disable no-bitwise */
import { Typography } from '@ag1/ui';
import { AlternateFlavor, AlternateFlavorConfig } from '@portal/processes/ShopV2/ShopV2.types';
import { CheckIcon } from '@portal/shared/studio/ContentPopup/Icons/CheckIcon';
import clsx from 'clsx';
import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './V53FlavorSelection.module.scss';

// Helper function to convert hex to rgba
const hexToRgba = (hex: string, alpha: number): string => {
  const bigint = parseInt(hex.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface V53FlavorSelectionProps {
  onChange: (flavorOption: AlternateFlavorConfig | AlternateFlavor) => void;
  activeVariantId: string;
  flavorVariants: AlternateFlavorConfig[] | AlternateFlavor[];
}

const V53FlavorSelection = ({
  onChange,
  flavorVariants,
  activeVariantId
}: V53FlavorSelectionProps) => {
  const { t } = useTranslation('sharedComponents');
  const selectFlavorText = t('v53.selectFlavorText');
  const [isInitialRender, setIsInitialRender] = useState(true);
  const uniqueId = useId();

  const radioGroupName = `flavor-selection-${uniqueId}`;

  useEffect(() => {
    // Set a small timeout to allow the browser to render the initial state
    // before enabling animations
    const timer = setTimeout(() => {
      setIsInitialRender(false);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  if (!flavorVariants?.length) return null;

  return (
    <div className={styles.container}>
      <Typography
        variant="subtitleMono"
        size="2"
        noPadding
        color="black-new"
        className={styles.selectFlavorText}>
        {`${selectFlavorText}:`}
      </Typography>

      <div className={styles.v53FlavorOptionsContainer}>
        {flavorVariants.map((flavorOption) => {
          const isActive = activeVariantId === flavorOption.id;

          // Determine hover color
          let hoverColor = 'rgba(255, 255, 255, 0.29)';
          if (flavorOption.hoverColor) {
            hoverColor = flavorOption.hoverColor;
          } else if (flavorOption.color) {
            hoverColor = hexToRgba(flavorOption.color, 0.1);
          }

          const inputId = `${radioGroupName}-${flavorOption.id}`;

          return (
            <label
              key={flavorOption.id}
              htmlFor={inputId}
              className={clsx(
                styles.flavorOptionItem,
                isActive && styles.activeFlavorOption,
                isInitialRender && styles.noAnimation
              )}>
              <input
                type="radio"
                id={inputId}
                name={radioGroupName}
                value={flavorOption.id}
                checked={isActive}
                onChange={() => {
                  if (onChange) onChange(flavorOption);
                }}
                className={styles.radioInput}
              />
              <div
                className={styles.flavorOptionInnerContainer}
                style={{ '--hover-color': hoverColor } as React.CSSProperties}>
                <div
                  className={clsx(styles.flavorBubble)}
                  style={flavorOption.color ? { backgroundColor: flavorOption.color } : undefined}
                />
                <div className={styles.flavorOptionContent}>
                  <div className={styles.checkIconWrapper}>
                    <CheckIcon stroke={flavorOption.fontColor || 'black'} />
                  </div>
                  <div
                    className={clsx(styles.flavorOptionTitle)}
                    style={{
                      color: isActive ? flavorOption.fontColor || 'black' : 'black' // Default active to black if no fontColor
                    }}
                    data-text={flavorOption.title}>
                    {flavorOption.title}
                  </div>
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default V53FlavorSelection;
